"use client";

import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { useCallback, useState } from "react";
import { trpc } from "@/lib/trpc/react";

/**
 * Hook for block/unblock actions with optimistic updates
 * Follows the same pattern as useFollowActions
 */
export function useBlockActions() {
	const [isPending, setIsPending] = useState(false);
	const utils = trpc.useUtils();

	const blockUserMutation = trpc.social.blockUser.useMutation({
		onError: (error) => {
			toast({
				title: "Error",
				description: error.message || "Failed to block user",
				variant: "destructive",
			});
		},
		onSuccess: () => {
			// Invalidate any cached block status queries
			utils.social.getBlockStatus.invalidate();
		},
	});

	const unblockUserMutation = trpc.social.unblockUser.useMutation({
		onError: (error) => {
			toast({
				title: "Error",
				description: error.message || "Failed to unblock user",
				variant: "destructive",
			});
		},
		onSuccess: () => {
			// Invalidate any cached block status queries
			utils.social.getBlockStatus.invalidate();
		},
	});

	const blockUser = useCallback(
		async (targetUserId: string, reason?: string) => {
			setIsPending(true);
			try {
				await blockUserMutation.mutateAsync({
					blockedUserId: targetUserId,
					reason,
				});
				return { success: true };
			} catch (error) {
				return {
					success: false,
					error:
						error instanceof Error ? error.message : "Failed to block user",
				};
			} finally {
				setIsPending(false);
			}
		},
		[blockUserMutation],
	);

	const unblockUser = useCallback(
		async (targetUserId: string) => {
			setIsPending(true);
			try {
				await unblockUserMutation.mutateAsync({
					unblockedUserId: targetUserId,
				});
				return { success: true };
			} catch (error) {
				return {
					success: false,
					error:
						error instanceof Error ? error.message : "Failed to unblock user",
				};
			} finally {
				setIsPending(false);
			}
		},
		[unblockUserMutation],
	);

	return {
		blockUser,
		unblockUser,
		isPending,
	};
}
